import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import * as React from "react";

import {
  INSTAGRAM_FOLLOWED,
  INSTAGRAM_JOINED_CHANNEL,
} from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

const steps = [
  {
    label: "Complete Your Profile 🪪",
    description: `Fill in a few details to get personalized brand recommendations tailored to your content.`,
  },
  {
    label: (
      <>
        Connect On Instagram{" "}
        <Box component="i" className="fa-brands fa-instagram" sx={{ ml: 1 }} />
      </>
    ),
    description:
      "Connect with Bento on Instagram to curate your brand pitch list with your favorite creators, and get pitching tips and support from our team anytime!",
  },
  {
    label: "Create a Pitch Template ✍",
    description:
      "Upload your own template or use our tools to help you write one. This email will be sent to brands you're interested in working with.",
  },
  {
    label: "Send Your First Pitch 📩",
    description: `Select a brand, make any edits to your email, and hit send—all through Bento.`,
  },
];

const getStepName = (index: number) => {
  if (index === 0) {
    return "Profile";
  } else if (index === 1) {
    return "Connect On Instagram";
  } else if (index === 2) {
    return "Create Template";
  } else if (index === 3) {
    return "Email Brand";
  }
};

export default function OnboardingStepper({
  totalEmailsSent,
}: {
  totalEmailsSent: number;
}) {
  const [activeStep, setActiveStep] = React.useState(0);
  const { currentOrg } = React.useContext(OrganizationUserContext);
  const {
    setProfileDialogOpen,
    setCreateTemplateOnboardingDialogOpen,
    setEmailBrandDialogOpen,
    setInstagramOnboardingDialogOpen,
    incompleteProfile,
    noTemplates,
  } = React.useContext(OnboardingDialogsContext);

  const handleStart = (index: number) => {
    trackEvent("Home Page Onboarding Step Get Started Clicked", {
      step: getStepName(index),
    });
    if (index === 0) {
      setProfileDialogOpen(true);
    } else if (index === 1) {
      setInstagramOnboardingDialogOpen(true);
    } else if (index === 2) {
      setCreateTemplateOnboardingDialogOpen(true);
    } else if (index === 3) {
      setEmailBrandDialogOpen(true);
    }
  };

  const handleSkip = (index: number) => {
    trackEvent("Home Page Onboarding Step Skip Button Clicked", {
      step: getStepName(index),
    });
    if (index === 3) {
      return;
    }
    setActiveStep(index + 1);
  };

  const hasClickedFollowed =
    localStorage.getItem(`${INSTAGRAM_FOLLOWED}-${currentOrg?.id}`) ===
      "true" ||
    localStorage.getItem(`${INSTAGRAM_JOINED_CHANNEL}-${currentOrg?.id}`) ===
      "true";

  React.useEffect(() => {
    if (incompleteProfile) {
      setActiveStep(0);
    } else if (!hasClickedFollowed) {
      setActiveStep(1);
    } else if (noTemplates) {
      setActiveStep(2);
    } else if (totalEmailsSent === 0) {
      setActiveStep(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, noTemplates, incompleteProfile, hasClickedFollowed]);

  const getCompletedStatus = (index: number) => {
    if (index === 0) {
      return !incompleteProfile;
    } else if (index === 1) {
      return !!hasClickedFollowed;
    } else if (index === 2) {
      return !noTemplates;
    } else {
      return totalEmailsSent > 0;
    }
  };

  const renderStepCard = (index: number) => {
    if (index === activeStep) {
      return (
        <Box sx={styles.cardBgActive}>
          <Grid container alignItems="center" sx={{ mb: 1 }} gap={1}>
            {getCompletedStatus(index) ? (
              <Box component="i" className="fa-regular fa-check" />
            ) : (
              <Box sx={[styles.stepNumber, styles.stepNumberActive]}>
                {index + 1}
              </Box>
            )}
            <Typography variant="h5">{steps[index]?.label}</Typography>
          </Grid>

          <Typography sx={styles.description}>
            {steps[index]?.description}
          </Typography>

          {getCompletedStatus(index) && (
            <Typography sx={{ color: "success.main", mb: 2 }}>
              You already completed this step
            </Typography>
          )}

          <Grid container gap={1} alignItems="center">
            {!getCompletedStatus(index) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStart(index);
                }}
                disableElevation
                variant="contained"
                sx={{ px: 2 }}
              >
                Start
              </Button>
            )}

            {index < 3 && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSkip(index);
                }}
                disableElevation
                variant={getCompletedStatus(index) ? "contained" : "text"}
              >
                {getCompletedStatus(index) ? "Next" : "Skip"}
              </Button>
            )}
          </Grid>
        </Box>
      );
    } else {
      return (
        <Box sx={styles.cardBgInactive(index < activeStep, index === 0)}>
          <Grid container alignItems="center" sx={{ mb: 1 }} gap={1}>
            <Box sx={[styles.stepNumber, styles.stepNumberInactive]}>
              {getCompletedStatus(index) ? (
                <Box component="i" className="fa-regular fa-check" />
              ) : (
                <>{index + 1}</>
              )}
            </Box>
            <Typography variant="h5">{steps[index]?.label}</Typography>
          </Grid>

          <Typography sx={styles.description}>
            {steps[index]?.description}
          </Typography>

          {getCompletedStatus(index) && (
            <Box sx={{ color: "success.main" }}>
              You already completed this step
            </Box>
          )}
        </Box>
      );
    }
  };

  return (
    <Box>
      {steps?.map((step, index) => (
        <Box sx={{ cursor: "pointer" }} onClick={() => setActiveStep(index)}>
          {renderStepCard(index)}
        </Box>
      ))}
    </Box>
  );
}
